import IconAdd from "@shared/components/icons/Add"
import { cx } from "@shared/components/untitledui/util/cx"
import { AddButton } from "@shared/unified-tiptap/editor-ui/add-button/AddButton"
import { CardPillButton } from "@shared/unified-tiptap/editor-ui/card-buttons/CardPillButton"
import { PLACEHOLDERS, placeholderStyle } from "@shared/unified-tiptap/placeholders"
import { useUiContext } from "@shared/unified-tiptap/unified-tiptap-ui-context"
import { useHoverChrome } from "@shared/unified-tiptap/use-hover-chrome"
import { findAddTargetPos } from "@shared/unified-tiptap/util/find-add-target-pos"
import { tracked } from "@supergrain/kernel/react"
import { NodeViewContent, NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react"
import { useState } from "react"

export const CommentContentComponent = tracked((props: ReactNodeViewProps) => {
  const { permissions, hideButtons, setCardOverlayOpen } = useUiContext()
  const { chromeVisible, chromeProps } = useHoverChrome("cardButtons")

  const [isAddOpen, setIsAddOpen] = useState(false)

  const setAddOpen = (isOpen: boolean) => {
    setIsAddOpen(isOpen)
    setCardOverlayOpen(isOpen)
  }

  const getAddTargetPos = (): number | null => {
    const contentPos = props.getPos()
    if (contentPos === undefined) return null

    const { head } = props.editor.state.selection
    if (head > contentPos && head < contentPos + props.node.nodeSize) {
      return findAddTargetPos(props.editor, head).pos
    }
    return contentPos + 1
  }

  return (
    <NodeViewWrapper
      className="relative flex flex-col"
      data-content-type="commentContent"
      {...chromeProps}
    >
      {permissions.canEdit && !hideButtons && (
        <div
          className={cx(
            "absolute top-0 right-0 z-10 flex flex-row items-center transition-opacity duration-150 ease-in-out",
            "card-pill-shadow",
            chromeVisible || isAddOpen
              ? "opacity-100"
              : "pointer-events-none opacity-0 focus-within:pointer-events-auto focus-within:opacity-100",
          )}
          contentEditable={false}
        >
          <AddButton
            editor={props.editor}
            mode="commentContent"
            getAddTargetPos={getAddTargetPos}
            // A comment is not a card, so there is nothing for the Add menu's own Comment
            // option to attach to — the mode leaves that option out.
            getCardId={() => null}
            onOpenChange={setAddOpen}
          >
            <CardPillButton
              ariaLabel="Add to comment"
              label="Add"
              icon={<IconAdd />}
              isMenuOpen={isAddOpen}
              side="left"
            />
          </AddButton>
        </div>
      )}

      <NodeViewContent
        className="unified-tiptap-card"
        style={placeholderStyle(PLACEHOLDERS.commentContent)}
      />
    </NodeViewWrapper>
  )
})
